require('../util/money-parser');

const pluginName = 'fundraisingSettingsPlugin';
const defaults = {
  callbacks: {
    initialized: () => {},
    setup: () => {},
  },
};

const selectors = {
  optInToggle: '[data-js-registrant-fundraising-settings-opt-in-toggle]',
  fundraiserFields: '[data-js-registrant-fundraising-settings-fundraiser-fields]',
  agreementCopy: '[data-js-registrant-fundraising-settings-agreement-copy]',
  selectedFundraisingOrganization: '[data-js-registrant-fundraising-settings-selected-fundraising-organization]',
  legacyPreferredOrganization: '#fundraising-preferred-organization',
  fundraisingCommitmentOrganization: '[data-js-registrant-fundraising-commitment-organization]',
  goalInput: '[data-js-registrant-fundraising-settings-goal]',
  teamLockCopy: '[data-js-registrant-fundraising-settings-team-lock-copy]',
  teamStateInput: 'input[name="teams[state]"]',
  teamsJoinFormControl: '.js-teams-join-form-control',
  teamFundraisingOptIn: 'input[name="teams[is_fundraising_team]"]',
  selectedTeamFundraisingOrganization: '[name="teams[selected_fundraising_organization]"]',
  singleFundraisingOrganization: '[data-js-registrant-fundraising-settings-single-fundraising-organization]',
};
const classes = {
  hidden: 'd-none hidden',
};

/**
 * Parse a goal amount using the same locale-aware money parser as donations.
 * @param {string|number} value
 * @returns {number|null}
 */
const parseGoalAmount = (value) => {
  if (value === null || typeof value === 'undefined') {
    return null;
  }

  const stringValue = String(value).trim();

  if (!stringValue) {
    return null;
  }

  return window.parseMoney(stringValue, window.RR?.locale);
};

const getMinimumGoalErrorMessage = (data, minimumDisplay) => {
  const template = data.settings.i18n.fundraisingGoalBelowMinimum
    || 'Fundraising goal cannot be less than %amount%';

  return template.replace('%amount%', minimumDisplay);
};

const getMinimumGoalFromElement = ($element) => {
  if (!$element?.length) {
    return null;
  }

  const minimumGoal = parseGoalAmount($element.attr('data-minimum-goal'));
  const minimumDisplay = $element.attr('data-minimum-goal-display');

  if (!minimumGoal || minimumGoal <= 0 || !minimumDisplay) {
    return null;
  }

  return { minimumGoal, minimumDisplay };
};

const getGoalMinimumRequirements = (data) => {
  const {
    $goalInput,
    $selectedFundraisingOrganization,
    $legacyPreferredOrganization,
    $fundraisingCommitmentOrganization,
    $optInToggle,
  } = data.elements;

  if (!$goalInput?.length) {
    return null;
  }

  if ($optInToggle?.length && !$optInToggle.is(':checked')) {
    return null;
  }

  if ($fundraisingCommitmentOrganization?.length) {
    const selectedId = $selectedFundraisingOrganization.val();
    const $selectedOrganization = selectedId
      ? $fundraisingCommitmentOrganization.filter(`[data-id="${selectedId}"]`)
      : $fundraisingCommitmentOrganization.filter(':not(.hidden, .d-none)').first();

    return getMinimumGoalFromElement($selectedOrganization);
  }

  const goalMinimum = getMinimumGoalFromElement($goalInput);

  if (goalMinimum) {
    return goalMinimum;
  }

  const $preferredOrganization = $selectedFundraisingOrganization?.length
    ? $selectedFundraisingOrganization
    : $legacyPreferredOrganization;

  if (!$preferredOrganization?.length) {
    return null;
  }

  return getMinimumGoalFromElement($preferredOrganization.find('option:selected'));
};

/**
 * Reflect the minimum in the input's constraint validation state so that submitting reports
 * the field the same way the browser reports an unmet required field.
 * @param {jQuery} $goalInput
 * @param {string} message
 */
const setGoalValidity = ($goalInput, message) => {
  const goalInput = $goalInput?.[0];

  if (goalInput && typeof goalInput.setCustomValidity === 'function') {
    goalInput.setCustomValidity(message);
  }
};

const validateGoalInput = (data, { showError = true } = {}) => {
  const { $goalInput } = data.elements;
  const { registrationPlugin } = data.settings;

  if (!$goalInput?.length) {
    return true;
  }

  const requirements = getGoalMinimumRequirements(data);
  const goalAmount = requirements ? parseGoalAmount($goalInput.val()) : null;

  const isBelowMinimum = requirements !== null
    && (goalAmount === null || goalAmount < requirements.minimumGoal);

  if (!isBelowMinimum) {
    setGoalValidity($goalInput, '');
    registrationPlugin?.clearFieldError?.($goalInput);

    return true;
  }

  const errorMessage = getMinimumGoalErrorMessage(data, requirements.minimumDisplay);

  setGoalValidity($goalInput, errorMessage);

  if (showError) {
    registrationPlugin?.showFieldError?.($goalInput, errorMessage);
  }

  return false;
};

/**
 * When a minimum applies, prefill blank/zero/below-minimum goals. When it does not,
 * clear a goal that was auto-filled for a previous minimum (e.g. switching sub-events).
 * @param data
 */
const syncGoalWithMinimumIfBelow = (data) => {
  const { $goalInput } = data.elements;
  const { registrationPlugin } = data.settings;

  if (!$goalInput?.length) {
    return;
  }

  const previousMinimum = data.state.applicableMinimumGoal ?? null;
  const requirements = getGoalMinimumRequirements(data);

  if (!requirements) {
    const goalAmount = parseGoalAmount($goalInput.val());

    if (
      previousMinimum !== null
      && goalAmount !== null
      && goalAmount === previousMinimum
    ) {
      $goalInput.val(0);
      setGoalValidity($goalInput, '');
      registrationPlugin?.clearFieldError?.($goalInput);
    }

    data.state.applicableMinimumGoal = null;
    return;
  }

  data.state.applicableMinimumGoal = requirements.minimumGoal;

  const goalAmount = parseGoalAmount($goalInput.val());

  if (goalAmount !== null && goalAmount >= requirements.minimumGoal) {
    return;
  }

  $goalInput.val(requirements.minimumGoal);
};

/**
 * Re-sync goal when the applicable minimum changes (e.g. charity or opt-in toggle).
 * @param data
 */
const refreshGoalMinimum = (data) => {
  syncGoalWithMinimumIfBelow(data);
  validateGoalInput(data, { showError: true });
};

const bindGoalValidation = (data) => {
  const { $goalInput } = data.elements;

  if (!$goalInput?.length) {
    return;
  }

  $goalInput.off('.fundraisingGoalValidation');

  let inputTimeout;

  const scheduleValidation = () => {
    window.clearTimeout(inputTimeout);
    inputTimeout = window.setTimeout(() => {
      validateGoalInput(data, { showError: true });
    }, 500);
  };

  $goalInput.on('blur.fundraisingGoalValidation', () => {
    validateGoalInput(data, { showError: true });
  });

  $goalInput.on('input.fundraisingGoalValidation', scheduleValidation);
};

const bindGoalValidationRefresh = (data) => {
  const {
    $selectedFundraisingOrganization,
    $legacyPreferredOrganization,
    $optInToggle,
  } = data.elements;

  const $preferredOrganization = $selectedFundraisingOrganization?.length
    ? $selectedFundraisingOrganization
    : $legacyPreferredOrganization;

  if ($preferredOrganization?.length) {
    $preferredOrganization.off('change.fundraisingGoalValidation');
    $preferredOrganization.on('change.fundraisingGoalValidation', () => refreshGoalMinimum(data));
  }

  if ($optInToggle?.length) {
    $optInToggle.off('change.fundraisingGoalValidation');
    $optInToggle.on('change.fundraisingGoalValidation', () => refreshGoalMinimum(data));
  }
};

/**
 * Get the elements. New layout has opt-in toggle, fundraiser fields, org selector; old layout does not.
 * @param data
 * @returns {{}}
 */
const getElements = (data) => {
  const $fundraiserFields = data.elements.$element.find(selectors.fundraiserFields);
  const teamCharityLockAttribute = $fundraiserFields.attr('data-team-charity-lock');

  data.state.hasTeamCharityLockSetting = typeof teamCharityLockAttribute !== 'undefined';
  data.state.teamCharityLock = teamCharityLockAttribute === '1';

  return {
    $element: data.elements.$element,
    $optInToggle: data.elements.$element.find(selectors.optInToggle),
    $fundraiserFields,
    $agreementCopy: data.elements.$element.find(selectors.agreementCopy),
    $selectedFundraisingOrganization: $fundraiserFields.find(selectors.selectedFundraisingOrganization),
    $legacyPreferredOrganization: data.elements.$element.find(selectors.legacyPreferredOrganization),
    $fundraisingCommitmentOrganization: $fundraiserFields.find(selectors.fundraisingCommitmentOrganization),
    $goalInput: data.elements.$element.find(selectors.goalInput),
    $teamLockCopy: $fundraiserFields.find(selectors.teamLockCopy),
    $singleFundraisingOrganization: $fundraiserFields.find(selectors.singleFundraisingOrganization),
  };
};

const getSelect2Elements = ($select) => {
  const select2 = $select.data('select2');
  const $container = select2?.$container?.length
    ? select2.$container
    : $select.next('.select2-container');

  return {
    $container,
    $focusTarget: $container.find('.select2-selection'),
  };
};

const preventTeamLockedOptOut = (event) => event.preventDefault();

const unlockTeamOrganization = (data) => {
  const {
    $optInToggle,
    $selectedFundraisingOrganization,
    $teamLockCopy,
  } = data.elements;
  const { $container, $focusTarget } = getSelect2Elements($selectedFundraisingOrganization);

  if (data.state.lockedByTeam && $selectedFundraisingOrganization.length) {
    $container.css('pointer-events', '').removeAttr('aria-disabled');
    $focusTarget.removeAttr('aria-disabled');

    if (data.state.teamCharityLockTabIndex === null) {
      $focusTarget.removeAttr('tabindex');
    } else if (typeof data.state.teamCharityLockTabIndex !== 'undefined') {
      $focusTarget.attr('tabindex', data.state.teamCharityLockTabIndex);
    }
  }

  $optInToggle
    .off('click.teamCharityLock', preventTeamLockedOptOut)
    .removeAttr('aria-disabled');
  $teamLockCopy.addClass(classes.hidden);
  data.state.lockedByTeam = false;
  delete data.state.teamCharityLockTabIndex;
};

const applyTeamOrganization = (data, fundraisingOrganizationUuid) => {
  const {
    $optInToggle,
    $selectedFundraisingOrganization,
    $singleFundraisingOrganization,
    $teamLockCopy,
  } = data.elements;

  if (!data.state.teamCharityLock) {
    return;
  }

  const organizationUuid = fundraisingOrganizationUuid
    ? String(fundraisingOrganizationUuid)
    : null;
  const $matchingOption = organizationUuid
    ? $selectedFundraisingOrganization.find('option').filter((index, option) => (
      $(option).attr('data-fundraising-organization-uuid') === organizationUuid
    )).first()
    : $();
  const $matchingSingleOrganization = organizationUuid
    ? $singleFundraisingOrganization.filter((index, input) => (
      $(input).attr('data-fundraising-organization-uuid') === organizationUuid
    )).first()
    : $();

  if (!$matchingOption.length && !$matchingSingleOrganization.length) {
    unlockTeamOrganization(data);
    return;
  }

  if ($optInToggle.length && !$optInToggle.is(':checked')) {
    $optInToggle.prop('checked', true).trigger('change');
  }

  const wasLockedByTeam = data.state.lockedByTeam;

  $optInToggle
    .attr('aria-disabled', 'true')
    .off('click.teamCharityLock', preventTeamLockedOptOut)
    .on('click.teamCharityLock', preventTeamLockedOptOut);
  data.state.lockedByTeam = true;

  if (!$matchingOption.length) {
    return;
  }

  $selectedFundraisingOrganization.val($matchingOption.val()).trigger('change');

  const { $container, $focusTarget } = getSelect2Elements($selectedFundraisingOrganization);

  if (!wasLockedByTeam) {
    data.state.teamCharityLockTabIndex = $focusTarget.attr('tabindex') ?? null;
  }

  $container.css('pointer-events', 'none').attr('aria-disabled', 'true');
  $focusTarget.attr({
    'aria-disabled': 'true',
    tabindex: '-1',
  });
  $teamLockCopy.removeClass(classes.hidden);
};

const getCurrentTeamFundraisingOrganizationUuid = () => {
  const $checkedState = $(selectors.teamStateInput)
    .filter(':checked, [type="hidden"]')
    .first();
  const teamState = $checkedState.val();

  if (teamState === 'joining-existing' || teamState === 'joining-reserved') {
    return $(selectors.teamsJoinFormControl)
      .find('option:selected')
      .attr('data-fundraising-organization-uuid') || null;
  }

  if (teamState === 'creating') {
    const $teamFundraisingOptIn = $(selectors.teamFundraisingOptIn);
    if ($teamFundraisingOptIn.length && !$teamFundraisingOptIn.is(':checked')) {
      return null;
    }

    return $(selectors.selectedTeamFundraisingOrganization).val() || null;
  }

  if (!teamState || teamState === 'no-team') {
    return null;
  }

  return $checkedState.attr('data-fundraising-organization-uuid') || null;
};

/**
 * Calls the provided custom callback and triggers the provided custom plugin event (if they exist)
 * @param data
 * @param hookName
 * @param parameters
 */
const triggerHook = (data, hookName, ...parameters) => {
  data.elements.$element
    .trigger(`${pluginName}.${hookName}`, [data.elements.$element, data, ...parameters]);
  data.settings.callbacks[hookName]
    .call(data.elements.$element, data.elements.$element, data, ...parameters);
};

/**
 * Toggle the visibility of the plugin
 * @param data
 * @param isVisible
 */
const toggleVisibility = (data, isVisible) => {
  data.elements.$element.toggleClass(classes.hidden, !isVisible);
};

/**
 * Serialize the fundraising settings
 * @param data
 * @returns {string}
 */
const serialize = (data) => {
  const formData = data.elements.$element.serializeArray();
  return $.param(formData);
};

/**
 * Cancel any active requests being made
 * @param data
 */
const cancelActiveRequests = (data) => {
  if (data.requests.validate) {
    data.requests.validate.abort();
  }
};

/**
 * Clear the plugin's contents
 */
const clear = (data) => {
  cancelActiveRequests(data);
  if (data.state.lockedByTeam) {
    unlockTeamOrganization(data);
  }
  if (data.state.hasTeamCharityLockSetting) {
    $(document).off('teams.fundraisingOrganizationChanged.fundraisingSettings');
  }
  data.elements.$element.empty();
  data.setup = false;
};

/**
 * Show the selected fundraising organization (new layout only)
 * @param data
 */
const showSelectedFundraisingOrganization = (data) => {
  if (!data.elements.$fundraisingCommitmentOrganization.length) {
    return;
  }

  data.elements.$fundraisingCommitmentOrganization.addClass(classes.hidden);

  const selectedFundraisingOrganization = data.elements.$selectedFundraisingOrganization.val();

  if (selectedFundraisingOrganization) {
    data.elements.$fundraisingCommitmentOrganization
      .filter(`[data-id="${selectedFundraisingOrganization}"]`)
      .removeClass(classes.hidden);
  }
};

/**
 * Toggle the visibility of the fundraiser fields (new layout only)
 * @param data
 */
const toggleFundraiserFields = (data) => {
  if (!data.elements.$optInToggle.length) {
    return;
  }

  if (data.state.lockedByTeam && !data.elements.$optInToggle.is(':checked')) {
    data.elements.$optInToggle.prop('checked', true);
  }

  const hasOptedIn = data.elements.$optInToggle.is(':checked');

  data.elements.$fundraiserFields
    .toggleClass(classes.hidden, !hasOptedIn)
    .prop('disabled', !hasOptedIn);

  data.elements.$agreementCopy.toggleClass(classes.hidden, !hasOptedIn);
};

/**
 * Setup the plugin. Supports both old and new layouts; new layout elements are optional.
 * @param data
 * @param html
 */
const setup = (data, html) => {
  if (data.state.lockedByTeam) {
    unlockTeamOrganization(data);
  }

  data.elements.$element.html(html);

  $.extend(data.elements, getElements(data));

  if (data.elements.$optInToggle.length) {
    data.elements.$optInToggle.on('change', () => toggleFundraiserFields(data));
    toggleFundraiserFields(data);
  }

  if (data.elements.$selectedFundraisingOrganization.length) {
    data.elements.$selectedFundraisingOrganization.select2({
      width: '100%',
      language: {
        searching: () => data.settings.i18n.loading,
        noResults: () => {
          const $search = data.elements.$selectedFundraisingOrganization
            .data('select2').dropdown.$search;
          if (!$search.val()) {
            return data.settings.i18n.searchByOrganizationName;
          }
          return data.settings.i18n.noResultsFound;
        },
      },
    });

    data.elements.$selectedFundraisingOrganization.on('change', () => {
      showSelectedFundraisingOrganization(data);

      const $select = data.elements.$selectedFundraisingOrganization;
      if ($select.val()) {
        $select.closest('.form-group').removeClass('has-error');
      }
    });

    showSelectedFundraisingOrganization(data);
  }

  data.elements.$element.find('[data-toggle="tooltip"]').tooltip();

  bindGoalValidation(data);
  bindGoalValidationRefresh(data);
  syncGoalWithMinimumIfBelow(data);
  validateGoalInput(data, { showError: false });

  if (data.state.hasTeamCharityLockSetting) {
    $(document).off('teams.fundraisingOrganizationChanged.fundraisingSettings');

    if (data.state.teamCharityLock) {
      $(document).on(
        'teams.fundraisingOrganizationChanged.fundraisingSettings',
        (event, fundraisingOrganizationUuid) => {
          applyTeamOrganization(data, fundraisingOrganizationUuid);
        },
      );
      applyTeamOrganization(data, getCurrentTeamFundraisingOrganizationUuid());
    }
  }

  data.setup = true;

  triggerHook(data, 'setup');
};

const methods = {
  toggleVisibility,
  setup,
  initializeContent: setup,
  clear,
  cancelActiveRequests,
};

const getMethods = {
  serialize: (data) => {
    if (!data.setup) {
      return '';
    }

    return serialize(data);
  },
  validateGoal: (data) => {
    if (!data.setup) {
      return true;
    }

    return validateGoalInput(data, { showError: true });
  },
};

/**
 * Call one of our custom plugin methods on each given jQuery object
 * @param $element
 * @param method
 * @param methodParameters
 * @returns {jQuery}
 */
const callPluginMethod = ($element, method, ...methodParameters) => {
  const data = $element.data(pluginName);

  if (data) {
    methods[method](data, ...methodParameters);
  }

  return $element;
};

/**
 * Fire up
 * @param $element
 * @param options
 */
const initialize = ($element, options) => {
  if ($element.data(pluginName)) {
    return;
  }

  const settings = $.extend(true, {}, defaults, options);
  const elements = {
    $element,
  };
  const requests = {};
  const state = {};
  const data = {
    settings,
    elements,
    requests,
    state,
    setup: false,
  };

  data.elements.$element.data(pluginName, data);
  triggerHook(data, 'initialized');
};

/**
 * Plugin
 * @param methodOrOptions
 * @param methodParameters
 * @returns {jQuery}
 */
const plugin = function plugin(methodOrOptions, ...methodParameters) {
  if (getMethods[methodOrOptions]) {
    const $element = $(this);
    const data = $element.data(pluginName);

    if (!data) {
      return false;
    }

    return getMethods[methodOrOptions](data, ...methodParameters);
  }

  if (methods[methodOrOptions]) {
    return this
      .each((index, element) => callPluginMethod($(element), methodOrOptions, ...methodParameters));
  }

  if (typeof methodOrOptions === 'object' || !methodOrOptions) {
    return this.each((index, element) => initialize($(element), methodOrOptions));
  }

  return $.error(`Method ${methodOrOptions} does not exist in ${pluginName}`);
};

plugin.methods = methods;
plugin.getMethods = getMethods;

$(() => {
  if (pluginName in $.fn) {
    return;
  }

  $.extend($.fn, {
    [pluginName]: plugin,
  });
});
